'use client';
import React from 'react';

interface Credential {
  label: string;
  value: string;
  detail: string;
}

const credentials: Credential[] = [
  {
    label: 'Certification',
    value: 'US CMA Candidate',
    detail: 'Part 2 cleared. Pursuing the globally recognized Certified Management Accountant designation from IMA.',
  },
  {
    label: 'Methodology',
    value: 'CMA-Level Rigor',
    detail: 'Every engagement follows management accounting best practices — structured, documented, and verifiable.',
  },
  {
    label: 'Commitment',
    value: 'Transparent & Honest',
    detail: 'No inflated credentials. Clear communication about scope, timelines, and deliverables on every project.',
  },
];

const whyPoints = [
  {
    title: 'Structured Approach',
    desc: 'Every engagement follows a defined workflow — intake, execution, review, and delivery — so nothing falls through the cracks.',
  },
  {
    title: 'Affordable Expertise',
    desc: 'Freelance pricing without the overhead of a firm. You get CMA-level methodology at rates that make sense for small businesses.',
  },
  {
    title: 'Secure & Confidential',
    desc: 'Your financial data is handled with strict confidentiality. Secure intake portal, encrypted file handling, and professional discretion.',
  },
  {
    title: 'Clear Deliverables',
    desc: 'You always know what you\'re getting. Every service comes with defined outputs, timelines, and a review round included.',
  },
];

export default function AboutSection() {
  return (
    <section id="about" className="bg-white py-16 sm:py-24 lg:py-32">
      <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 sm:gap-16 lg:gap-24 items-start">
          {/* Left column */}
          <div>
            <p
              className="text-primary font-sans text-xs font-semibold tracking-widest uppercase mb-4"
              style={{ letterSpacing: '3px' }}
            >
              About SYHJ
            </p>
            <h2
              className="mb-5 sm:mb-6"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 'clamp(26px, 6vw, 48px)',
                fontWeight: 700,
                lineHeight: '1.15',
                letterSpacing: '-1px',
                color: '#111111',
              }}
            >
              Financial clarity,
              <br />
              built on honest
              <br />
              foundations.
            </h2>
            <div className="w-12 h-0.5 bg-primary mb-6 sm:mb-8" />
            <p
              className="text-brand-grey700 text-sm sm:text-base mb-5 sm:mb-6 leading-relaxed"
              style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", lineHeight: '1.75' }}
            >
              SYHJ is a freelance financial services practice offering bookkeeping, bank reconciliation, and financial statement preparation to small businesses, startups, and sole proprietors.
            </p>
            <p
              className="text-brand-grey700 text-sm sm:text-base mb-8 sm:mb-10 leading-relaxed"
              style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", lineHeight: '1.75' }}
            >
              As a US CMA candidate who has cleared Part 2 of the exam, I bring structured management accounting methodology to every engagement — with full transparency about my credentials and experience level. No overpromising. Just precise, reliable financial work.
            </p>

            {/* Credentials */}
            <div className="space-y-5 sm:space-y-6">
              {credentials.map((cred) => (
                <div key={cred.label} className="flex gap-4 sm:gap-5 group">
                  <div className="flex-shrink-0 w-1 bg-brand-grey200 group-hover:bg-primary transition-colors duration-300" />
                  <div>
                    <p
                      className="text-xs font-semibold text-primary uppercase tracking-widest mb-1"
                      style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", letterSpacing: '2px' }}
                    >
                      {cred.label}
                    </p>
                    <p
                      className="font-semibold text-brand-dark mb-1"
                      style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '16px' }}
                    >
                      {cred.value}
                    </p>
                    <p
                      className="text-brand-grey700 text-sm leading-relaxed"
                      style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                    >
                      {cred.detail}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right column - Why SYHJ */}
          <div>
            <p
              className="text-primary font-sans text-xs font-semibold tracking-widest uppercase mb-4"
              style={{ letterSpacing: '3px' }}
            >
              Why choose SYHJ
            </p>
            <h3
              className="mb-8 sm:mb-10"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 'clamp(22px, 5vw, 36px)',
                fontWeight: 700,
                lineHeight: '1.2',
                letterSpacing: '-0.5px',
                color: '#111111',
              }}
            >
              What makes working
              <br />
              with me different.
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {whyPoints.map((point) => (
                <div
                  key={point.title}
                  className="bg-brand-grey50 p-5 sm:p-6 transition-all duration-500 hover:-translate-y-1"
                  style={{ transitionTimingFunction: 'cubic-bezier(0.22, 0.61, 0.36, 1)' }}
                >
                  <div className="w-8 h-0.5 bg-primary mb-4" />
                  <h4
                    className="font-semibold text-brand-dark mb-2 sm:mb-3"
                    style={{
                      fontFamily: "'Playfair Display', Georgia, serif",
                      fontSize: '17px',
                      lineHeight: '1.3',
                    }}
                  >
                    {point.title}
                  </h4>
                  <p
                    className="text-brand-grey700 text-sm leading-relaxed"
                    style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", lineHeight: '1.7' }}
                  >
                    {point.desc}
                  </p>
                </div>
              ))}
            </div>

            {/* CMA badge */}
            <div
              className="mt-6 sm:mt-8 p-5 sm:p-6 border border-brand-grey200 flex items-center gap-4 sm:gap-5"
            >
              <div
                className="w-12 h-12 sm:w-14 sm:h-14 bg-primary flex items-center justify-center flex-shrink-0"
              >
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
                  <path d="M14 3l2.5 7.5H24l-6.5 4.5 2.5 7.5L14 18l-6 4.5 2.5-7.5L4 10.5h7.5L14 3z" fill="white" />
                </svg>
              </div>
              <div>
                <p
                  className="font-bold text-brand-dark"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '15px' }}
                >
                  US CMA Candidate
                </p>
                <p
                  className="text-brand-grey700 text-xs sm:text-sm mt-0.5"
                  style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                >
                  Institute of Management Accountants · Part 2 Cleared
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
